import { Backdrop } from "@mui/material";
import { isFulfilled, nanoid } from "@reduxjs/toolkit";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import LoadingSpinner from "../../../../common/components/functional/loadingSpinner/LoadingSpinner";
import isEmpty from "../../../../common/helpers/isEmpty";
import { Recommender, Talent } from "../../../talents/talentAPI";
import {
  selectTalentById,
  updateTalentRecommenders,
} from "../../../talents/talentsSlice";
import { UpdateTalentRecommenders } from "../../../talents/types/commands";
import RecommenderForm from "./RecommenderForm";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";

type Props = {
  onClose: () => void;
};

type FormValues = {
  name: string;
  title: string;
  company: string;
  phone: string;
  email: string;
  additionalInfo: string;
};

const TalentAddRecommender = ({ onClose }: Props) => {
  const { t } = useTranslation();

  const { talentId } = useParams();
  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const defaultValues: FormValues = {
    name: "",
    title: "",
    company: "",
    phone: "",
    email: "",
    additionalInfo: "",
  };

  const methods = useForm<FormValues>({
    defaultValues: defaultValues,
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { errors, isDirty },
  } = methods;

  const onSubmit = (formValues: FormValues) => {
    setIsLoading(true);

    if (talentId) {
      const existingValues = talent.recommenders?.values || [];
      const existingVersion = talent.recommenders?.version || 0;

      const addedValue: Recommender = {
        id: nanoid(),
        name: formValues.name,
        title: formValues.title,
        company: formValues.company,
        phone: formValues.phone,
        email: formValues.email,
        additionalInfo: formValues.additionalInfo,
      };

      const updatedRecommenders: Recommender[] =
        existingValues.length > 0
          ? [...existingValues, addedValue]
          : [addedValue];

      const commandPayload: UpdateTalentRecommenders = {
        talentId: talent.id,
        version: existingVersion,
        recommenders: updatedRecommenders,
      };

      dispatch(updateTalentRecommenders(commandPayload)).then((action) => {
        setIsLoading(false);
        onClose();
        return isFulfilled(action);
      });
    }
  };

  const onSave = () => handleSubmit(onSubmit)();

  const onCancel = () => onClose();

  const saveDisabled = !isEmpty(errors) || !isDirty || isLoading;

  return (
    <DialogContentWrapper
      title={t("recommenders:recommender")}
      margin
      saveDisabled={saveDisabled}
      onSave={onSave}
      onCancel={onCancel}
    >
      <Backdrop
        open={isLoading}
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.4)",
          zIndex: 1000,
        }}
      >
        <LoadingSpinner />
      </Backdrop>
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <RecommenderForm />
        </UnsavedChangesProvider>
      </FormProvider>
    </DialogContentWrapper>
  );
};

export default TalentAddRecommender;
